import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { Modal, ModalBody, ModalDescription, ModalFooter, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import type { ProblemResponse } from "@/lib/http";
import type { SubscriptionModel } from "@/features/common/api/useSubscription";
import { useSubscriptionCreate, type SubscriptionCreateRequestModel } from "@/features/subscriptions/api/useSubscriptionCreate";
import { PlanPicker, type PlanSelection } from "@/features/subscriptions/components/PlanPicker";
import { PaymentCardPicker } from "@/features/subscriptions/components/PaymentCardPicker";
import {
	formatSubscriptionDate,
	getCurrentPaymentCardId,
	getSubscribeModalCopy,
	isSubscribeSelectionUnchanged,
	type PlanPreference,
	type SubscribeMode
} from "@/features/subscriptions/SubscriptionLogic";

const TERMS_AND_CONDITIONS_URL = "https://fyendalscollection.com/terms-and-conditions";

export interface SubscribeOptions {
	readonly mode: SubscribeMode;
	readonly planPreference: PlanPreference;
	readonly preselectedPlanId: string | null;
	readonly promoCode?: string;
}

export interface ModalSubscribeProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	subscription: SubscriptionModel;
	options: SubscribeOptions;
}

export const ModalSubscribe: FC<ModalSubscribeProps> = props => {
	const {
		open,
		onOpenChange,
		subscription,
		options
	} = props;

	const subscriptionCreate = useSubscriptionCreate();
	const copy = useMemo(() => getSubscribeModalCopy(options.mode, subscription), [options.mode, subscription]);
	useNotifyMutationSuccess(subscriptionCreate, copy.successText);

	useEffect(() => {
		if (open) {
			subscriptionCreate.reset();
		}
	}, [open]);

	useEffect(() => {
		if (subscriptionCreate.isSuccess) {
			onOpenChange(false);
		}
	}, [subscriptionCreate.isSuccess]);

	return (
		<Modal open={open} onOpenChange={onOpenChange} preventCloseOnInteractOutside={subscriptionCreate.isPending}>
			<ModalHeader>
				<ModalTitle>{copy.title}</ModalTitle>
				<ModalDescription>{copy.description}</ModalDescription>
			</ModalHeader>

			<ModalSubscribeContent
				subscription={subscription}
				options={options}
				submitText={copy.submitText}
				isPending={subscriptionCreate.isPending}
				error={subscriptionCreate.error}
				onCancel={() => onOpenChange(false)}
				onSubmit={subscriptionCreate.mutate} />
		</Modal>
	);
};

interface ModalSubscribeContentProps {
	subscription: SubscriptionModel;
	options: SubscribeOptions;
	submitText: string;
	isPending: boolean;
	error: ProblemResponse | null;
	onCancel: () => void;
	onSubmit: (requestModel: SubscriptionCreateRequestModel) => void;
}

const ModalSubscribeContent: FC<ModalSubscribeContentProps> = props => {
	const {
		subscription,
		options,
		submitText,
		isPending,
		error,
		onCancel,
		onSubmit
	} = props;

	const [planSelection, setPlanSelection] = useState<PlanSelection | null>(null);
	const [paymentCardId, setPaymentCardId] = useState<string | null>(() => getCurrentPaymentCardId(subscription));

	const isUnchanged = !!planSelection
		&& !!paymentCardId
		&& isSubscribeSelectionUnchanged(subscription, planSelection.plan.planId, paymentCardId, planSelection.promoCode);

	const canSubmit = !!planSelection && !!paymentCardId && !isUnchanged && !isPending;

	const onConfirm = useCallback(() => {
		if (!planSelection || !paymentCardId) {
			return;
		}

		onSubmit({
			planId: planSelection.plan.planId,
			paymentCardId,
			promoCode: planSelection.promoCode
		});
	}, [planSelection, paymentCardId, onSubmit]);

	return (
		<>
			<ModalBody>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-3">
						<div className="text-sm font-medium">Plan</div>
						<PlanPicker
							value={planSelection}
							onChange={setPlanSelection}
							planPreference={options.planPreference}
							preselectedPlanId={options.preselectedPlanId}
							initialPromoCode={options.promoCode}
							disabled={isPending} />
					</div>

					<div className="flex flex-col gap-3">
						<div className="text-sm font-medium">Payment Card</div>
						<PaymentCardPicker value={paymentCardId} onChange={setPaymentCardId} disabled={isPending} />
					</div>

					{
						planSelection && (
							<SubscribeSummary
								mode={options.mode}
								subscription={subscription}
								price={planSelection.plan.price} />
						)
					}

					<p className="text-xs text-muted-foreground">
						Subscription renewal charges will occur 2 days before the auto renewal date.
						By continuing, you agree to our <a className="text-primary underline-offset-4 hover:underline" href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noreferrer">Terms and Conditions</a>.
					</p>
				</div>
			</ModalBody>

			{
				error && (
					<div className="px-4 pt-4 @lg:px-0">
						<ErrorDisplay error={error} />
					</div>
				)
			}

			<ModalFooter>
				<Button variant="outline" disabled={isPending} onClick={onCancel}>
					Cancel
				</Button>
				<Button disabled={!canSubmit} onClick={onConfirm}>
					{isPending ? <Spinner /> : submitText}
				</Button>
			</ModalFooter>
		</>
	);
};

interface SubscribeSummaryProps {
	mode: SubscribeMode;
	subscription: SubscriptionModel;
	price: number;
}

const SubscribeSummary: FC<SubscribeSummaryProps> = props => {
	const {
		mode,
		subscription,
		price
	} = props;

	const isDeferred = mode === "ChangePlan";

	return (
		<div className="flex flex-col gap-1 rounded-md bg-muted/50 p-3 text-sm">
			<div className="flex justify-between gap-3">
				<span className="text-muted-foreground">Due today</span>
				<MoneyDisplay className="font-medium" mode="Standard" money={{ value: isDeferred ? 0 : price, currency: "USD" }} />
			</div>

			{
				isDeferred && (
					<div className="flex justify-between gap-3">
						<span className="text-muted-foreground">
							{
								subscription.activeSubscription
									? `From ${formatSubscriptionDate(subscription.activeSubscription.expiryDate)}`
									: "At next renewal"
							}
						</span>
						<MoneyDisplay mode="Standard" money={{ value: price, currency: "USD" }} />
					</div>
				)
			}
		</div>
	);
};
